import {
  createContext,
  useContext,
  useState,
  useEffect,
  useRef,
  type ReactNode,
  createElement,
} from "react";
import type {
  IAuthProvider,
  ILicenseProvider,
  AuthSession,
} from "@retreat/platform-contracts";
import { getClient } from "@retreats/supabase-adapter";
import { STORAGE_KEYS, TIMING, AUTH_URL_PARAMS, APP_URL_PARAMS } from "../constants";
import { clearLocalSessionData } from "../state/sessionRepository";
import { syncManager } from "../services/SyncManager";
import { setCurrentSyncUserId } from "../services/sessionSyncService";
import { identifyUser, setCurrentWave, track } from "../analytics/posthog";

export interface AuthContextValue {
  session: AuthSession | null;
  authLoading: boolean;
  auth: IAuthProvider;
  license: ILicenseProvider;
  wave: string | null;
  isAdmin: boolean;
}

export const AuthContext = createContext<AuthContextValue | null>(null);

export function useAuthContext(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuthContext must be used within AuthProvider");
  return ctx;
}

interface AuthProviderProps {
  auth: IAuthProvider;
  license: ILicenseProvider;
  children: ReactNode;
}

type ProfileRow = { wave: string | null; is_admin: boolean };

async function resolveWave(
  db: any,
  userId: string,
  storedWave: string | null,
  pendingWave: string | null,
): Promise<string | null> {
  if (storedWave) return storedWave;
  if (!pendingWave) return null;
  await db.from("profiles").update({ wave: pendingWave }).eq("id", userId);
  return pendingWave;
}

async function fetchAndApplyProfile(
  userId: string,
  setWave: (w: string | null) => void,
  setIsAdmin: (v: boolean) => void,
  isNewLogin: boolean,
): Promise<void> {
  try {
    const db = getClient();
    const pendingWave = sessionStorage.getItem(STORAGE_KEYS.PENDING_WAVE);

    const { data: profile } = await db
      .from("profiles")
      .select("wave, is_admin")
      .eq("id", userId)
      .single() as { data: ProfileRow | null };

    const effectiveWave = await resolveWave(db, userId, profile?.wave ?? null, pendingWave);

    if (pendingWave) sessionStorage.removeItem(STORAGE_KEYS.PENDING_WAVE);

    setWave(effectiveWave);
    setIsAdmin(profile?.is_admin ?? false);
    setCurrentWave(effectiveWave);
    identifyUser(userId, effectiveWave);

    if (isNewLogin) {
      track("login_completed", { wave: effectiveWave });
    }
  } catch {
    // Profile fetch failing is non-fatal — analytics will just lack wave context.
  }
}

/** Remove Supabase auth params left in the URL after token processing. */
function cleanAuthUrl(): void {
  const params = new URLSearchParams(window.location.search);
  params.delete(AUTH_URL_PARAMS.TOKEN_HASH);
  params.delete(AUTH_URL_PARAMS.CODE);
  params.delete(AUTH_URL_PARAMS.TYPE);
  const q = params.toString();
  const clean = q ? `${window.location.pathname}?${q}` : window.location.pathname;
  if (clean !== window.location.pathname + window.location.search) {
    window.history.replaceState({}, "", clean);
  }
  // Also clear any hash left by the implicit flow.
  if (window.location.hash) {
    window.history.replaceState({}, "", window.location.pathname + (q ? `?${q}` : ""));
  }
}

/** True when a different user (or a first-time user on a device with stale data) signs in. */
function isUserSwitch(storedUserId: string | null, nextUserId: string): boolean {
  return storedUserId !== nextUserId;
}

/** True on the first sign-in event in this session (prev was logged out, next is logged in). */
function isNewLogin(prevUserId: string | null, nextUserId: string | null): nextUserId is string {
  return nextUserId !== null && prevUserId === null;
}

export function AuthProvider({
  auth,
  license,
  children,
}: AuthProviderProps): ReturnType<typeof createElement> {
  const [session, setSession] = useState<AuthSession | null>(null);
  const [authLoading, setAuthLoading] = useState(true);
  const [wave, setWave] = useState<string | null>(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const prevUserIdRef = useRef<string | null>(null);

  useEffect(() => {
    let active = true;

    // Capture ?wave= from the invite URL before the SPA strips query params.
    const urlWave = new URLSearchParams(window.location.search).get(APP_URL_PARAMS.WAVE);
    if (urlWave) sessionStorage.setItem(STORAGE_KEYS.PENDING_WAVE, urlWave);

    // Fast path: returning user already has a session in localStorage.
    // onAuthStateChange handles ALL token-exchange flows (magic link, OAuth, PKCE).
    auth
      .getSession()
      .then((s) => {
        if (!active || s === null) return;
        setSession(s);
        setAuthLoading(false);
        void fetchAndApplyProfile(s.userId, setWave, setIsAdmin, false);
      })
      .catch(() => { /* non-fatal; onAuthStateChange clears loading */ });

    // Primary auth state driver. Fires INITIAL_SESSION after all URL token
    // processing is complete — covers implicit hash, PKCE token_hash, and OAuth code.
    const unsubscribe = auth.onAuthStateChange((s) => {
      if (!active) return;
      const prevUserId = prevUserIdRef.current;
      const nextUserId = s?.userId ?? null;
      prevUserIdRef.current = nextUserId;
      setSession(s);
      setAuthLoading(false);

      if (isNewLogin(prevUserId, nextUserId)) {
        const lastUserId = localStorage.getItem(STORAGE_KEYS.LAST_USER_ID);
        if (isUserSwitch(lastUserId, nextUserId)) clearLocalSessionData();
        localStorage.setItem(STORAGE_KEYS.LAST_USER_ID, nextUserId);

        // Register current user BEFORE drain so drainSyncQueue can purge stale
        // queue items from the previous account without waiting for
        // syncLocalSessionsToRemote (which fires on the next React render cycle).
        setCurrentSyncUserId(nextUserId);

        void syncManager.pull(nextUserId);
        void syncManager.drain();
        void fetchAndApplyProfile(nextUserId, setWave, setIsAdmin, true);
      }

      // Clean up any Supabase auth params the library left in the URL.
      if (s) cleanAuthUrl();
    });

    // Safety net: if onAuthStateChange never fires (bad network, expired token), un-block the UI.
    const timeout = setTimeout(() => {
      if (active) setAuthLoading(false);
    }, TIMING.AUTH_INIT_TIMEOUT_MS);

    return () => {
      active = false;
      unsubscribe();
      clearTimeout(timeout);
    };
  }, [auth]);

  return createElement(
    AuthContext.Provider,
    { value: { session, authLoading, auth, license, wave, isAdmin } },
    children,
  );
}
